/**
 * adscripcion-edicion.ts — Web data-layer of the adscription modal's IMMEDIATE changes.
 *
 * The order and the name of "Otra" only touch the personal-data mirror rows of units the researcher already
 * has, so they need no request: they go through the same datos-personales endpoints the rest of the profile uses
 * (`PUT :slug/datos-personales/:idTipo/:secuencia` and `PUT :slug/datos-personales/orden`). This module exists
 * because the modal is opened from places that are NOT the edit shell ("Mis solicitudes" on the public profile), so
 * it cannot lean on the shell's handlers. The structural part (add, remove, change the principal) is NOT here: it
 * is a request (`createSolicitud`) or the admin direct PUT (`onAplicarDirecto`).
 */

import { InvestigadorDatosPersonalesResponseSchema } from '@siiv/schemas';
import { apiClient, apiGet } from '@/lib/apiClient';
import { errorDeRespuesta } from '@/lib/api-error-message';
import { runWithInvestigatorProfileRevalidation } from '@/lib/investigator-revalidation-client';
import type { CambiosEdicion, FilaEspejoDeUnidad } from '@/components/investigador/solicitudes/adscripcion/adscripcionEdicion';

const CODIGOS_DE_UNIDAD = new Set(['unidad', 'unidad_adicional']);

const MENSAJE_FALLO_CAMBIOS = 'No se pudieron guardar los cambios de las unidades. Intenta de nuevo.';

/** GET `:slug/datos-personales`, reduced to the mirror rows of the units (the owner or an admin only). */
export async function getFilasDeUnidades(slug: string): Promise<FilaEspejoDeUnidad[]> {
	const json = await apiGet<unknown>(`/investigadores/${encodeURIComponent(slug)}/datos-personales`);
	return InvestigadorDatosPersonalesResponseSchema.parse(json)
		.datos.filter((dato) => CODIGOS_DE_UNIDAD.has(dato.codigo))
		.map((dato) => ({
			codigo: dato.codigo,
			valorTexto: dato.valorTexto,
			ordenVisual: dato.ordenVisual,
			idUnidad: dato.idUnidad,
			idTipoDatoPersonal: dato.idTipoDatoPersonal,
			secuencia: dato.secuencia,
		}));
}

async function escribir(slug: string, ruta: string, cuerpo: unknown): Promise<void> {
	const respuesta = await apiClient(`/investigadores/${encodeURIComponent(slug)}/datos-personales/${ruta}`, {
		method: 'PUT',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(cuerpo),
	});
	if (!respuesta.ok) throw await errorDeRespuesta(respuesta, MENSAJE_FALLO_CAMBIOS);
}

/**
 * Applies the immediate part of a staged edition: ONE write for the mirror row of "Otra" when it was renamed and ONE
 * order write for the whole list. The writes run one after the other and the first refusal stops the rest. It
 * revalidates the public caches once, at the end: the profile shows these rows. A unit has no visibility, so nothing
 * here ever sends `esPublico`. The caller refreshes what is open (`router.refresh()` + `emitirPerfilActualizado`),
 * see AGENTS.md.
 */
export async function aplicarCambiosInmediatos(slug: string, cambios: Pick<CambiosEdicion, 'orden' | 'renombre'>): Promise<void> {
	if (cambios.renombre === null && cambios.orden === null) return;

	await runWithInvestigatorProfileRevalidation(slug, async () => {
		if (cambios.renombre) {
			const { idTipoDatoPersonal, secuencia } = cambios.renombre.fila;
			await escribir(slug, `${idTipoDatoPersonal}/${secuencia}`, { valorTexto: cambios.renombre.nombre });
		}
		if (cambios.orden) {
			await escribir(slug, 'orden', {
				orden: cambios.orden.map((fila) => ({ idTipoDatoPersonal: fila.idTipoDatoPersonal, secuencia: fila.secuencia })),
			});
		}
	});
}
